/** Insights → Templates: every template in one place. It opens on the
 *  dashboard (every template's chart side by side) - always: there is no
 *  switch and nothing remembered (item 78, Segev 2026-11-01). A click on a
 *  card opens that template on its own (`?template=<id>`, pushed, so the
 *  browser's Back returns); "Back to dashboard" does the same, and the
 *  dashboard is found as it was left - filters, dates, scroll. On its own the
 *  list sits at the inline-start (name, plain answer type, its schedule's
 *  "Repeats" mark, last run, all its answers, and a live run's progress with
 *  Cancel); the template's results and actions at the inline-end. Create,
 *  edit, schedule, pause, run and delete all start here. A run is a row the server works: its progress is polled every 3 s
 *  while it moves (each poll also works a short slice of it on the server),
 *  backing off to 10 s when it does not, and leaving the page never stops it.
 *
 *  A person always sees what they ran (item 70, utils/templateRange): every
 *  chart names its date range and says how many answers it leaves out, with
 *  Show all; the page's untouched default range opens on the answers; a
 *  finished run widens the range to the days its answers cover. */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Modal, Progress, Select, Spin, Tooltip, message as antdMessage } from 'antd';
import dayjs from 'dayjs';
import {
  ArrowLeftOutlined, BarChartOutlined, CompressOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, ExpandOutlined,
  InfoCircleOutlined, LineChartOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined, PoweroffOutlined,
  SearchOutlined, SyncOutlined, TableOutlined,
} from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { formatRelativeTime } from '@/utils/relativeTime';
import insightsService from '../../services/insightsService';
import type {
  AgentOption, InsightFilters, InsightTemplate, InsightsQuota, LabelAggregation, RetroactiveRun, TagTimeseries, TenantNode,
} from '../../types/insights';
import { cover, holds, openingRange, type DayRange, type RangeFacts, type Span } from '../../utils/templateRange';
import { InsightsStickyBar, type FilterSlots } from '../InsightsStickyBar';
import { instantLabel } from '../reports/reportParts';
import { OutsideRange, UpdatedAt, rangeLabel } from './RangeParts';
import { RunTemplateDialog } from './RunTemplateDialog';
import { ScheduleMark, scheduleRule } from './ScheduleMark';
import { Takeaway, TemplateChart } from './TemplateChart';
import { TemplateEditorWindow } from './TemplateEditorWindow';
import { TemplateTrend, TREND_SERIES } from './TemplateTrend';
import { TemplatesDashboard } from './TemplatesDashboard';
import { CouldntWhy } from './CouldntWhy';
import {
  TYPE_ICON, errorMessage, filterParams, formatCount, saveAnswersCsv, scaleRange, scopeSummary, typeName, valueLabel,
} from './templateParts';
import '@modules/chatbots/components/ConnectorDialog.css';
import './Templates.css';

const ACTIVE = ['pending', 'running'];

/** What scrolls the page around `el` (the app's content pane, else the document). */
function scrollParent(el: Element | null): Element {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) return node;
  }
  return document.scrollingElement || document.documentElement;
}
const POLL_FIRST_MS = 3000;
const POLL_MAX_MS = 10000;
const PROGRESS_STEP = 100;   // conversations between chart refreshes while a run works

interface Props {
  templates: InsightTemplate[];
  loading: boolean;
  quota: InsightsQuota | null;
  tenantTree: TenantNode[];
  agentOptions: AgentOption[];
  /** The page's filters (organizations, agents, dates), as a row that can hold this tab's buttons (item 86). */
  scope?: (slots?: FilterSlots) => ReactNode;
  filters: InsightFilters;
  /** The person picked the dates themselves (the page's opening range no longer stands). */
  datesChosen: boolean;
  /** Set the page's date range (it only ever widens here). */
  onRange: (range: DayRange) => void;
  canCreate: boolean;
  /** Reload the templates and the quota (settles when both are back). */
  onChanged: () => Promise<unknown> | void;
}

/** The runs working the templates now, polled until they finish. */
function useRunProgress(templates: InsightTemplate[], onFinished: (template: InsightTemplate, run: RetroactiveRun) => void) {
  const [runs, setRuns] = useState<Record<string, RetroactiveRun>>({});
  const [started, setStarted] = useState<Record<string, string>>({});
  // Runs already announced: the template list may still name one until it reloads.
  const [done, setDone] = useState<Set<string>>(() => new Set());
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  const watch = useMemo(() => {
    const ids: Record<string, string> = {};
    templates.forEach((tpl) => { if (tpl.active_run_id) ids[tpl.template_id] = tpl.active_run_id; });
    Object.assign(ids, started);
    Object.keys(ids).forEach((templateId) => { if (done.has(ids[templateId])) delete ids[templateId]; });
    return ids;
  }, [templates, started, done]);
  const watchKey = JSON.stringify(Object.entries(watch).sort());

  useEffect(() => {
    const entries = Object.entries(watch);
    if (!entries.length) return;
    let cancelled = false;
    let timer: number | undefined;
    let delay = POLL_FIRST_MS;
    const seen: Record<string, number> = {};   // conversations processed at the last poll, per run
    const tick = async () => {
      const results = await Promise.all(entries.map(([templateId, runId]) => insightsService.getRetroactiveRun(runId)
        .then((run) => [templateId, run] as const).catch(() => [templateId, null] as const)));
      if (cancelled) return;
      // A poll that moved a run along keeps the pace: each one works a slice of it.
      const moved = results.some(([, run]) => run && run.conversations_processed > (seen[run.run_id] ?? -1));
      results.forEach(([, run]) => { if (run) seen[run.run_id] = run.conversations_processed; });
      setRuns((prev) => {
        const next = { ...prev };
        results.forEach(([templateId, run]) => { if (run) next[templateId] = run; });
        return next;
      });
      const finished = results.filter(([, run]) => run && !ACTIVE.includes(run.status));
      if (finished.length) {
        setDone((prev) => new Set([...prev, ...finished.map(([, run]) => run!.run_id)]));
        setStarted((prev) => {
          const next = { ...prev };
          finished.forEach(([templateId]) => { delete next[templateId]; });
          return next;
        });
        finished.forEach(([templateId, run]) => {
          const tpl = templates.find((x) => x.template_id === templateId);
          if (tpl && run) finishedRef.current(tpl, run);
        });
        return;   // what is left to watch changed: the effect starts again on it
      }
      delay = moved ? POLL_FIRST_MS : Math.min(POLL_MAX_MS, Math.round(delay * 1.5));
      timer = window.setTimeout(tick, delay);
    };
    tick();
    return () => { cancelled = true; window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchKey]);

  const active = useCallback((templateId: string): RetroactiveRun | null => {
    const run = runs[templateId];
    return watch[templateId] && run && run.run_id === watch[templateId] && ACTIVE.includes(run.status) ? run : null;
  }, [runs, watch]);
  const isRunning = useCallback((templateId: string) => !!watch[templateId], [watch]);
  const track = useCallback((templateId: string, run: RetroactiveRun) => {
    setRuns((prev) => ({ ...prev, [templateId]: run }));
    setStarted((prev) => ({ ...prev, [templateId]: run.run_id }));
  }, []);
  return { active, isRunning, track };
}

export function TemplatesTab({
  templates, loading, quota, tenantTree, agentOptions, filters, datesChosen, onRange, canCreate, onChanged, scope,
}: Props) {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const tabRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState<'chart' | 'table' | 'trend'>('chart');
  const [trend, setTrend] = useState<{ data: TagTimeseries | null; loading: boolean; error: string | null }>(
    { data: null, loading: false, error: null });
  const [aggregation, setAggregation] = useState<LabelAggregation | null>(null);
  const [aggregationLoading, setAggregationLoading] = useState(false);
  const [aggregationError, setAggregationError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editor, setEditor] = useState<{ open: boolean; template: InsightTemplate | null }>({ open: false, template: null });
  const [runFor, setRunFor] = useState<InsightTemplate | null>(null);
  const [deleting, setDeleting] = useState<InsightTemplate | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const sortedTemplates = useMemo(() => [...templates].sort((a, b) => (
    Number(b.is_system) - Number(a.is_system) || a.template_name.localeCompare(b.template_name)
  )), [templates]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? sortedTemplates.filter((x) => x.template_name.toLowerCase().includes(q)) : sortedTemplates;
  }, [sortedTemplates, search]);

  // The dashboard unless one template is asked for: nothing else decides it.
  const requestedId = searchParams.get('template');
  const dashboard = !requestedId;
  const selected = requestedId ? sortedTemplates.find((x) => x.template_id === requestedId) || null : null;
  const selectedRange = useMemo(() => (selected ? scaleRange(selected) : undefined), [selected]);
  const selectedType = selected?.response_type;
  const nameOf = useCallback((value: string) => valueLabel(t, selectedType || 'label', value), [t, selectedType]);
  /** Change the URL's template; `push` makes a new history entry (the browser's Back returns from it). */
  const goTo = useCallback((templateId: string | null, push: boolean) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (templateId) next.set('template', templateId); else next.delete('template');
      next.delete('view');   // the old Dashboard / One at a time switch
      return next;
    }, push ? { state: { fromDashboard: true } } : { replace: true, state: location.state });
  }, [setSearchParams, location.state]);
  // The list has answered at least once (its loading flag starts false).
  const seenLoading = useRef(false);
  if (loading) seenLoading.current = true;
  const listed = seenLoading.current && !loading;
  // An old link's ?view= goes, and a template that is not there (deleted, another
  // organization's, a list that failed) is the dashboard - never a spinner.
  useEffect(() => {
    if (searchParams.has('view') || (requestedId && listed && !selected)) {
      goTo(selected ? selected.template_id : null, false);
    }
  }, [searchParams, requestedId, listed, selected, goTo]);

  // Where the dashboard was scrolled when a card was opened: Back puts it there again.
  const dashboardSpot = useRef<{ el: Element; top: number } | null>(null);
  /** A dashboard card: that template on its own, as a new history entry. */
  const open = useCallback((templateId: string) => {
    const el = scrollParent(tabRef.current);
    dashboardSpot.current = { el, top: el.scrollTop };
    setView('chart');
    setExpanded(false);
    goTo(templateId, true);
  }, [goTo]);
  /** Another template from the list beside it: the same history entry. */
  const select = useCallback((templateId: string) => {
    setView('chart');
    goTo(templateId, false);
  }, [goTo]);
  /** Back to the dashboard: the browser's own Back when the dashboard is the
      entry behind this one (so Forward works too), else to it in place. */
  const backToDashboard = useCallback(() => {
    if ((location.state as { fromDashboard?: boolean } | null)?.fromDashboard) navigate(-1);
    else goTo(null, false);
  }, [location.state, navigate, goTo]);
  // However it comes back (this Back, the browser's, a deleted template) the
  // dashboard is whole - never expanded - and where it was left (its top after
  // a deep link); a template opens at its top.
  useEffect(() => {
    const spot = dashboardSpot.current;
    if (dashboard) setExpanded(false);
    window.requestAnimationFrame(() => {
      if (dashboard && spot) spot.el.scrollTop = spot.top;
      else scrollParent(tabRef.current).scrollTop = 0;
    });
  }, [dashboard]);

  // The range in force, read by the callbacks below without re-creating them.
  const rangeRef = useRef<DayRange>({ dateFrom: filters.dateFrom, dateTo: filters.dateTo });
  rangeRef.current = { dateFrom: filters.dateFrom, dateTo: filters.dateTo };
  const today = () => dayjs().format('YYYY-MM-DD');
  /** Show all: the range widened to the days the answers cover. */
  const showAll = useCallback((span: Span | null | undefined) => {
    if (span) onRange(cover(rangeRef.current, span, today()));
  }, [onRange]);
  // Opening on the answers: once, while the page's own range still stands.
  const opened = useRef(false);
  const datesChosenRef = useRef(datesChosen);
  datesChosenRef.current = datesChosen;
  const considerOpening = useCallback((facts: RangeFacts[]) => {
    if (opened.current) return;
    opened.current = true;
    const next = openingRange(rangeRef.current, datesChosenRef.current, facts, today());
    if (next) onRange(next);
  }, [onRange]);

  const onFinished = useCallback((tpl: InsightTemplate, run: RetroactiveRun) => {
    const n = formatCount((run.results_created || 0) + (run.results_updated || 0));
    // What the run wrote is what the person came to see: the range covers it.
    const widen = run.status !== 'failed' && run.answers_span && !holds(rangeRef.current, run.answers_span)
      ? cover(rangeRef.current, run.answers_span, today()) : null;
    if (run.status === 'completed') {
      antdMessage.success(widen
        ? t('insights.tpl.run.finishedWidened', { name: tpl.template_name, n, range: rangeLabel(widen) })
        : t('insights.tpl.run.finished', { name: tpl.template_name, n }));
    } else if (run.status === 'failed') antdMessage.error(t('insights.tpl.run.failed', { name: tpl.template_name }));
    else antdMessage.info(t('insights.tpl.run.stopped', { name: tpl.template_name }));
    if (widen) onRange(widen);
    onChanged();
    setRefreshKey((k) => k + 1);
  }, [onChanged, onRange, t]);
  const progress = useRunProgress(templates, onFinished);
  const selectedRun = selected ? progress.active(selected.template_id) : null;

  // The selected template's answers for the filters; while it runs, again every
  // PROGRESS_STEP conversations (not on every poll).
  const processed = selectedRun ? Math.floor(selectedRun.conversations_processed / PROGRESS_STEP) : -1;
  // The same steps for every template a run is answering (the dashboard's cards).
  const runsKey = sortedTemplates.map((x) => {
    const run = progress.active(x.template_id);
    return run ? Math.floor(run.conversations_processed / PROGRESS_STEP) : '';
  }).join(',');
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    if (!selected || dashboard) { setAggregation(null); return; }
    let cancelled = false;
    setAggregationLoading(true);
    setAggregationError(null);
    insightsService.getTemplateAggregations(selected.template_id, filterParams(filters))
      .then((a) => {
        if (cancelled) return;
        setAggregation(a.aggregation as LabelAggregation);
        considerOpening([a.aggregation as LabelAggregation]);
      })
      .catch((err) => { if (!cancelled) { setAggregation(null); setAggregationError(errorMessage(t, err)); } })
      .finally(() => { if (!cancelled) setAggregationLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.template_id, filtersKey, refreshKey, processed, dashboard]);

  // Categories over time (weekly), only while that view is shown.
  useEffect(() => {
    if (!selected || dashboard || view !== 'trend') return;
    let cancelled = false;
    setTrend((prev) => ({ ...prev, loading: true, error: null }));
    insightsService.getTagTimeseries(selected.template_id, { ...filterParams(filters), top_n: TREND_SERIES })
      .then((data) => { if (!cancelled) setTrend({ data, loading: false, error: null }); })
      .catch((err) => { if (!cancelled) setTrend({ data: null, loading: false, error: errorMessage(t, err) }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.template_id, view, filtersKey, refreshKey, processed, dashboard]);

  /** Pause a template's schedule (the rule is kept; nothing runs) or resume it.
   *  One change at a time: a second click before the page shows the first
   *  would send the same value again. */
  const [pausing, setPausing] = useState(false);
  const setPaused = async (tpl: InsightTemplate, paused: boolean) => {
    if (pausing) return;
    setPausing(true);
    try {
      await insightsService.updateTemplate(tpl.template_id, { schedule_paused: paused });
      antdMessage.success(t(paused ? 'insights.tpl.schedule.pausedToast' : 'insights.tpl.schedule.resumedToast',
        { name: tpl.template_name }));
      await onChanged();
    } catch (err) {
      antdMessage.error(errorMessage(t, err));
    } finally {
      setPausing(false);
    }
  };

  /** Switch a template off (it is never run - not on a schedule, not by hand - and
   *  keeps its answers) or back on. A switched-off template's panel says so
   *  and offers Turn on instead of Run now. */
  const setActive = async (tpl: InsightTemplate, active: boolean) => {
    try {
      await insightsService.updateTemplate(tpl.template_id, { is_active: active });
      antdMessage.success(t(active ? 'insights.tpl.results.switchedOn' : 'insights.tpl.results.switchedOff',
        { name: tpl.template_name }));
      onChanged();
    } catch (err) {
      antdMessage.error(errorMessage(t, err));
    }
  };

  const cancelRun = async (tpl: InsightTemplate) => {
    const run = progress.active(tpl.template_id);
    const runId = run?.run_id || tpl.active_run_id;
    if (!runId) return;
    try {
      await insightsService.cancelRetroactiveRun(runId);
    } catch (err) {
      antdMessage.error(errorMessage(t, err));
    }
  };

  const exportCsv = async () => {
    if (!selected) return;
    setExporting(true);
    const refused = await saveAnswersCsv(t, selected.template_id, filterParams(filters));
    setExporting(false);
    if (refused) antdMessage.error(refused);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await insightsService.deleteTemplate(deleting.template_id);
      antdMessage.success(t('insights.tpl.deleted', { name: deleting.template_name }));
      setDeleting(null);
      backToDashboard();
      onChanged();
    } catch (err) {
      antdMessage.error(errorMessage(t, err));
    } finally {
      setDeleteBusy(false);
    }
  };

  const onSaved = (saved: InsightTemplate, run: boolean) => {
    setEditor({ open: false, template: null });
    antdMessage.success(t('insights.tpl.saved', { name: saved.template_name }));
    (dashboard ? open : select)(saved.template_id);   // from the dashboard, Back returns to it
    onChanged();
    setRefreshKey((k) => k + 1);
    if (run) setRunFor(saved);
  };

  const manual = quota?.manual_runs;
  const scheduledQuota = quota?.auto_trigger;
  const exempt = !!quota?.is_exempt;

  const listRow = (tpl: InsightTemplate) => {
    const run = progress.active(tpl.template_id);
    const running = progress.isRunning(tpl.template_id);
    const isSelected = selected?.template_id === tpl.template_id;
    const total = run?.conversations_total || 0;
    return (
      <li key={tpl.template_id}>
        <button type="button" className={`tpl-row${isSelected ? ' is-selected' : ''}`} aria-current={isSelected || undefined}
                onClick={() => select(tpl.template_id)} data-testid={`template-row-${tpl.template_id}`}>
          <span className="tpl-row__icon" aria-hidden="true">{TYPE_ICON[tpl.response_type]}</span>
          <span className="tpl-row__body">
            <span className="tpl-row__top">
              <span className="tpl-row__name" dir="auto">{tpl.template_name}</span>
              <ScheduleMark template={tpl} compact />
              {!tpl.is_active && <span className="cx-pill tpl-row__pill">{t('insights.tpl.list.off')}</span>}
            </span>
            <span className="tpl-row__type">{typeName(t, tpl.response_type)}</span>
            {running ? (
              <span className="tpl-row__run">
                <span className="tpl-row__run-line">
                  <span>{t('insights.tpl.list.analyzing')}</span>
                  {total > 0 && <bdi>{formatCount(run?.conversations_processed || 0)} / {formatCount(total)}</bdi>}
                </span>
                <Progress percent={total ? Math.round(((run?.conversations_processed || 0) / total) * 100) : 0}
                          size="small" showInfo={false} status="active"
                          aria-label={t('insights.tpl.list.progress', { name: tpl.template_name })} />
              </span>
            ) : (
              <span className="tpl-row__meta">
                {tpl.last_run_at
                  ? t('insights.tpl.list.lastRun', { when: formatRelativeTime(tpl.last_run_at) })
                  : t('insights.tpl.list.notRunYet')}
                {tpl.total_results_count > 0 && <> · {t('insights.tpl.list.answersInAll', {
                  count: tpl.total_results_count, n: formatCount(tpl.total_results_count),
                })}</>}
              </span>
            )}
          </span>
        </button>
        {running && (
          <button type="button" className="tpl-link tpl-row__cancel" onClick={() => cancelRun(tpl)}
                  aria-label={t('insights.tpl.list.cancelRunOf', { name: tpl.template_name })}>
            {t('common.cancel')}
          </button>
        )}
      </li>
    );
  };

  const scopeChip = (tpl: InsightTemplate) => scopeSummary(t, tpl.scope, tenantTree,
    tenantTree.find((n) => n.tenant_id === tpl.tenant_id)?.tenant_name);

  const results = () => {
    if (!selected) return null;
    const running = progress.isRunning(selected.template_id);
    const answered = aggregation?.total || 0;
    const unanswered = aggregation?.unanswered || 0;
    const neverRun = !selected.last_run_at && selected.total_results_count === 0 && !running;
    const outside = aggregation?.outside || 0;
    const scheduled = !!selected.frequency && selected.frequency !== 'none';
    const nextRun = selected.next_runs?.[0]?.at;
    return (
      <section className="tpl-results" aria-labelledby="tpl-results-title" data-testid="template-results">
        <header className="tpl-results__head">
          <div className="tpl-results__titles">
            <div className="tpl-results__title-row">
              <h2 id="tpl-results-title" className="tpl-results__title" dir="auto">{selected.template_name}</h2>
              <span className="cx-pill tpl-results__type">
                <span aria-hidden="true">{TYPE_ICON[selected.response_type]}</span>
                {typeName(t, selected.response_type)}
              </span>
              <ScheduleMark template={selected} />
            </div>
            <p className="tpl-results__question"><bdi>“</bdi><span dir="auto">{selected.question}</span><bdi>”</bdi></p>
          </div>
          <div className="tpl-results__actions">
            <button type="button" className="cx-btn" onClick={() => setEditor({ open: true, template: selected })}
                    data-testid="template-edit">
              <EditOutlined aria-hidden />{t('insights.tpl.results.edit')}
            </button>
            <button type="button" className="cx-btn cx-btn--primary" onClick={() => setRunFor(selected)}
                    disabled={running || !selected.is_active} data-testid="template-run">
              <PlayCircleOutlined aria-hidden />{t('insights.tpl.results.runNow')}
            </button>
            <Tooltip title={t(selected.is_active ? 'insights.tpl.results.switchOff' : 'insights.tpl.results.turnOn')}>
              <button type="button" className="cx-btn tpl-icon-only" disabled={running && selected.is_active}
                      aria-label={t(selected.is_active ? 'insights.tpl.results.switchOffNamed' : 'insights.tpl.results.turnOnNamed',
                        { name: selected.template_name })}
                      aria-pressed={!selected.is_active}
                      onClick={() => setActive(selected, !selected.is_active)} data-testid="template-switch">
                {selected.is_active ? <PauseCircleOutlined aria-hidden /> : <PoweroffOutlined aria-hidden />}
              </button>
            </Tooltip>
            {!selected.is_system && (
              <Tooltip title={t('insights.tpl.results.delete')}>
                <button type="button" className="cx-btn cx-btn--danger tpl-icon-only"
                        aria-label={t('insights.tpl.results.deleteNamed', { name: selected.template_name })}
                        onClick={() => setDeleting(selected)} data-testid="template-delete">
                  <DeleteOutlined aria-hidden />
                </button>
              </Tooltip>
            )}
          </div>
        </header>

        <p className="tpl-results__stats">
          <span>{selected.last_run_at
            ? t('insights.tpl.results.lastRun', { when: formatRelativeTime(selected.last_run_at) })
            : t('insights.tpl.list.notRunYet')}</span>
          <span aria-hidden="true">·</span>
          <span>{selected.input_mode === 'full_conversation'
            ? t('insights.tpl.results.readsFull') : t('insights.tpl.results.readsSummaries')}</span>
          <span aria-hidden="true">·</span>
          <span>{scopeChip(selected)}</span>
        </p>

        {scheduled && (
          <p className="tpl-results__schedule" data-testid="template-schedule-line">
            <span>{selected.schedule_paused
              ? t('insights.tpl.schedule.pausedRule', { rule: scheduleRule(t, selected, language) })
              : scheduleRule(t, selected, language)}</span>
            {!selected.schedule_paused && nextRun && (
              <>
                <span aria-hidden="true">·</span>
                <span>{t('insights.tpl.schedule.next', { when: instantLabel(nextRun, selected.zone || undefined) })}</span>
              </>
            )}
            {selected.is_active && (
              <button type="button" className="tpl-link" onClick={() => setPaused(selected, !selected.schedule_paused)}
                      disabled={pausing} aria-busy={pausing || undefined} data-testid="template-schedule-pause">
                {selected.schedule_paused ? t('insights.tpl.schedule.resume') : t('insights.tpl.schedule.pause')}
              </button>
            )}
          </p>
        )}

        {!selected.is_active && (
          <div className="tpl-notice tpl-notice--info" role="status" data-testid="template-inactive">
            <InfoCircleOutlined aria-hidden />
            <span className="tpl-notice__body">{t('insights.tpl.errors.TEMPLATE_INACTIVE')}</span>
            <button type="button" className="tpl-link" onClick={() => setActive(selected, true)}
                    data-testid="template-turn-on">{t('insights.tpl.results.turnOn')}</button>
          </div>
        )}

        {running && (
          <div className="tpl-notice tpl-notice--info" role="status">
            <SyncOutlined spin aria-hidden />
            <span className="tpl-notice__body">
              {selectedRun && selectedRun.conversations_total > 0
                ? t('insights.tpl.results.runningProgress', {
                  done: formatCount(selectedRun.conversations_processed), total: formatCount(selectedRun.conversations_total),
                })
                : t('insights.tpl.results.runningStarting')}
            </span>
            <button type="button" className="tpl-link" onClick={() => cancelRun(selected)}>{t('common.cancel')}</button>
          </div>
        )}

        {aggregationLoading && !aggregation ? (
          <div className="tpl-results__loading"><Spin /></div>
        ) : aggregationError ? (
          <p className="tpl-error" role="alert">{aggregationError}</p>
        ) : answered === 0 && unanswered === 0 ? (
          <div className="tpl-empty">
            <span className="tpl-empty__icon" aria-hidden="true"><BarChartOutlined /></span>
            <h3>{neverRun || running ? t('insights.tpl.results.noAnswersYet')
              : t('insights.tpl.results.noAnswersInRange', { range: rangeLabel(filters) })}</h3>
            {outside > 0 ? (
              <OutsideRange outside={outside} onShowAll={() => showAll(aggregation?.span)} testId="template-outside" />
            ) : (
              <p className="tpl-muted">{running ? t('insights.tpl.results.answersArriving')
                : neverRun ? t('insights.tpl.results.runOnceHint') : t('insights.tpl.results.periodHint')}</p>
            )}
            {neverRun && selected.is_active && (
              <button type="button" className="cx-btn cx-btn--primary" onClick={() => setRunFor(selected)}>
                <PlayCircleOutlined aria-hidden />{t('insights.tpl.results.runNow')}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="tpl-chart-box">
              <div className="tpl-chart-box__top">
                <span className="tpl-chart-box__lead">
                  {aggregation && <Takeaway type={selected.response_type} aggregation={aggregation}
                                            range={selectedRange} />}
                  <span className="tpl-chart-box__count" data-testid="template-answered-count">
                    {t('insights.tpl.results.answeredInRange', {
                      count: answered, n: formatCount(answered), range: rangeLabel(filters),
                    })}
                  </span>
                </span>
                {selected.response_type !== 'free_text' && answered > 0 && (
                  <span className="tpl-views" role="group" aria-label={t('insights.tpl.results.viewAs')}>
                    {([
                      ['chart', <BarChartOutlined key="i" aria-hidden />, t('insights.tpl.results.viewChart')],
                      ['table', <TableOutlined key="i" aria-hidden />, t('insights.tpl.results.viewTable')],
                      ...(selected.response_type === 'label' || selected.response_type === 'multi_label'
                        ? [['trend', <LineChartOutlined key="i" aria-hidden />, t('insights.tpl.results.viewTrend')]] : []),
                    ] as [typeof view, JSX.Element, string][]).map(([key, icon, label]) => (
                      <button key={key} type="button" className={`cx-btn cx-btn--sm${view === key ? ' is-on' : ''}`}
                              aria-pressed={view === key} onClick={() => setView(key)}>
                        {icon}{label}
                      </button>
                    ))}
                  </span>
                )}
              </div>
              <OutsideRange outside={outside} onShowAll={() => showAll(aggregation?.span)} testId="template-outside" />
              {answered > 0 && aggregation && view === 'trend' && (
                <TemplateTrend data={trend.data} loading={trend.loading} error={trend.error} large
                               nameOf={nameOf} order={aggregation.category_order} />
              )}
              {answered > 0 && aggregation && view !== 'trend' && (
                <TemplateChart type={selected.response_type} aggregation={aggregation} view={view}
                               size="large" range={selectedRange} />
              )}
            </div>
            <div className="tpl-results__under">
              <span className="tpl-results__under-facts">
                <UpdatedAt at={aggregation?.updated_at} />
                {unanswered > 0 && (
                  <CouldntWhy split={aggregation?.unanswered_split} total={unanswered}
                              className="cx-pill tpl-results__couldnt" testId="template-couldnt">
                    {t('insights.tpl.results.couldnt', { count: unanswered, n: formatCount(unanswered) })}
                  </CouldntWhy>
                )}
              </span>
              <span className="tpl-results__under-actions">
                <button type="button" className="cx-btn cx-btn--sm" onClick={() => setExpanded((v) => !v)}>
                  {expanded ? <CompressOutlined aria-hidden /> : <ExpandOutlined aria-hidden />}
                  {expanded ? t('insights.tpl.results.backToList') : t('insights.tpl.results.expand')}
                </button>
                <button type="button" className="cx-btn cx-btn--sm" onClick={exportCsv} disabled={exporting || answered === 0}
                        data-testid="template-export">
                  <DownloadOutlined aria-hidden />{t('insights.tpl.results.exportCsv')}
                </button>
              </span>
            </div>
          </>
        )}
      </section>
    );
  };

  const newButton = canCreate && (
    <button type="button" className="cx-btn cx-btn--primary cx-btn--sm"
            onClick={() => setEditor({ open: true, template: null })} data-testid="template-new">
      <PlusOutlined aria-hidden />{t('insights.tpl.list.new')}
    </button>
  );

  const emptyState = !loading && (
    <section className="tpl-results tpl-empty">
      <span className="tpl-empty__icon" aria-hidden="true"><BarChartOutlined /></span>
      <h3>{t('insights.tpl.results.noTemplates')}</h3>
      {canCreate && (
        <button type="button" className="cx-btn cx-btn--primary" onClick={() => setEditor({ open: true, template: null })}>
          <PlusOutlined aria-hidden />{t('insights.tpl.list.new')}
        </button>
      )}
    </section>
  );

  return (
    <div ref={tabRef} className={`tpl-tab${expanded ? ' is-expanded' : ''}${dashboard ? ' is-dashboard' : ''}`}
         data-testid="templates-tab">
      {expanded && scope?.()}
      {!expanded && (
        <InsightsStickyBar testId="templates-sticky" filters={scope} views={dashboard ? undefined : (
          <button type="button" className="cx-btn cx-btn--sm ins-sticky__named" onClick={backToDashboard}
                  data-testid="templates-back">
            <ArrowLeftOutlined className="rtl-flip" aria-hidden />{t('insights.tpl.results.backToDashboard')}
          </button>
        )} actions={newButton || undefined} />
      )}

      {/* Kept while one template is open, so Back finds it as it was. */}
      <div className="tpl-tab__board" hidden={!dashboard}>
        {sortedTemplates.length ? (
          <TemplatesDashboard templates={sortedTemplates} filters={filters} refreshKey={`${refreshKey}:${runsKey}`}
                              shown={dashboard} isRunning={progress.isRunning} onOpen={open}
                              onLoaded={considerOpening} onShowAll={showAll} />
        ) : emptyState}
      </div>

      {!dashboard && !expanded && (
        <section className="tpl-list" aria-labelledby="tpl-list-title">
          <div className="tpl-list__head">
            <h2 id="tpl-list-title" className="tpl-list__title">
              {t('insights.tpl.list.title')} <span className="tpl-list__count"><bdi>{formatCount(templates.length)}</bdi></span>
            </h2>
          </div>
          {!exempt && (scheduledQuota || manual) && (
            <p className="tpl-list__quota">
              {scheduledQuota && <span>{t('insights.tpl.list.scheduledQuota', {
                used: formatCount(scheduledQuota.used), limit: formatCount(scheduledQuota.limit) })}</span>}
              {manual && <span>{t('insights.tpl.list.manualQuota', { used: formatCount(manual.used), limit: formatCount(manual.limit) })}</span>}
            </p>
          )}
          {/* On a narrow screen the list is one picker, so the chart is not a screen of templates away. */}
          <Select className="tpl-list__picker" value={selected?.template_id} onChange={select}
                  aria-label={t('insights.tpl.list.pick')} showSearch optionFilterProp="label"
                  options={sortedTemplates.map((tpl) => ({ value: tpl.template_id, label: tpl.template_name }))}
                  data-testid="template-picker" />
          {templates.length > 6 && (
            <Input className="tpl-list__search" allowClear size="small" dir="auto" prefix={<SearchOutlined aria-hidden />} value={search}
                   onChange={(e) => setSearch(e.target.value)} placeholder={t('insights.tpl.list.search')}
                   aria-label={t('insights.tpl.list.search')} />
          )}
          {loading && !templates.length ? <div className="tpl-results__loading"><Spin /></div> : (
            <ul className="tpl-list__items">
              {shown.map(listRow)}
              {!shown.length && <li className="tpl-muted tpl-list__none">{t('insights.tpl.list.none')}</li>}
            </ul>
          )}
        </section>
      )}

      {!dashboard && (selected ? results() : <div className="tpl-results__loading"><Spin /></div>)}

      {editor.open && (
        <TemplateEditorWindow template={editor.template} tenantTree={tenantTree} agentOptions={agentOptions}
                              quota={quota} onClose={() => setEditor({ open: false, template: null })}
                              onSaved={onSaved} />
      )}
      <RunTemplateDialog open={!!runFor} template={runFor} quota={quota} onClose={() => setRunFor(null)}
                         onStarted={(run) => {
                           if (runFor) progress.track(runFor.template_id, run);
                           setRunFor(null);
                           onChanged();
                         }} />
      <Modal
        open={!!deleting}
        onCancel={() => setDeleting(null)}
        title={t('insights.tpl.delete.title')}
        okText={t('insights.tpl.delete.confirm')}
        okButtonProps={{ danger: true, loading: deleteBusy, 'data-testid': 'template-delete-confirm' } as any}
        cancelText={t('common.cancel')}
        onOk={confirmDelete}
        destroyOnHidden
      >
        {deleting && (
          <>
            <p>{deleting.total_results_count > 0
              ? t('insights.tpl.delete.body', {
                name: deleting.template_name, count: deleting.total_results_count, n: formatCount(deleting.total_results_count),
              })
              : t('insights.tpl.delete.bodyNoAnswers', { name: deleting.template_name })}</p>
            {progress.isRunning(deleting.template_id) && <p>{t('insights.tpl.delete.running')}</p>}
          </>
        )}
      </Modal>
    </div>
  );
}
